import * as React from "react";
import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import TableHead from "@mui/material/TableHead";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Collapse from "@mui/material/Collapse";
import { apiGateway } from "../../../repositories/api.gateway";
import { apiCadastro } from "../../../repositories/api.cadastro";
import { useQuery } from "@tanstack/react-query";
import { Auditoria, GenericObject } from "../../../interfaces";
import { getForms } from "../../../services/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { formatarDataBrasilia } from "../../../utils/formatDate";
import { appendMunicipioToUrl } from "../../../hooks/useMunicipioFromUrl";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

interface ISearchData {
  dataInicio: moment.Moment | null;
  dataFim: moment.Moment | null;
}

type AuditoriaEnvelope = {
  total: number;
  page: number;
  pages: number;
  limit: number;
  data: Auditoria[];
};

type Order = "asc" | "desc";

export default function TabelaAuditoria() {
  const initialQueryState = React.useMemo(() => {
    const usp = new URLSearchParams(window.location.search);

    const limitQS = parseInt(usp.get("limit") || "", 10);
    const rowsPerPage = !isNaN(limitQS) && limitQS > 0 ? limitQS : 5;

    const offsetQS = parseInt(usp.get("offset") || "", 10);
    const page = !isNaN(offsetQS) && offsetQS >= 0 ? Math.floor(offsetQS / rowsPerPage) : 0;

    const orderByQS = usp.get("order_by");
    const orderQS = usp.get("order");
    const allowedOrderBy = ["id", "data", "camada", "descricao", "usuario"];

    const orderBy: keyof Auditoria | "" =
      orderByQS && allowedOrderBy.includes(orderByQS) ? (orderByQS as keyof Auditoria) : "";
    const order: Order = orderQS === "asc" || orderQS === "desc" ? (orderQS as Order) : "asc";

    const camada = usp.get("camada") ?? "";
    const propriedade = usp.get("propriedade") ?? "";
    const valor_propriedade = usp.get("valor_propriedade") ?? "";
    const descricao = usp.get("descricao") ?? "";
    const usuario = usp.get("usuario") ?? "";
    const diQS = usp.get("data_inicio");
    const dfQS = usp.get("data_fim");

    return {
      page,
      rowsPerPage,
      orderBy,
      order,
      camada,
      propriedade,
      valor_propriedade,
      descricao,
      usuario,
      dataInicio: diQS ? moment(diQS, "YYYY-MM-DDTHH:mm:ss.SSSSSS", true) : null,
      dataFim: dfQS ? moment(dfQS, "YYYY-MM-DDTHH:mm:ss.SSSSSS", true) : null,
    };
  }, []);

  const [page, setPage] = React.useState(initialQueryState.page);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialQueryState.rowsPerPage);
  const [camada, setCamada] = React.useState<string>(initialQueryState.camada);
  const [propriedade, setPropriedade] = React.useState<string>(initialQueryState.propriedade);
  const [valuePropriedade, setValuePropriedade] = React.useState<string>(initialQueryState.valor_propriedade);
  const [dataSearch, setDataSearch] = React.useState<ISearchData>({
    dataInicio: initialQueryState.dataInicio,
    dataFim: initialQueryState.dataFim,
  });
  const [descricao, setDescricao] = React.useState<string>(initialQueryState.descricao);
  const [usuario, setUsuario] = React.useState<string>(initialQueryState.usuario);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = React.useState<boolean>(false);
  const [order, setOrder] = React.useState<Order>(initialQueryState.order);
  const [orderBy, setOrderBy] = React.useState<keyof Auditoria | "">(initialQueryState.orderBy);
  const isInitialized = React.useRef(true);

  useEffect(() => {
    if (!isInitialized.current) return;

    const usp = new URLSearchParams();

    usp.set("limit", String(rowsPerPage));
    usp.set("offset", String(rowsPerPage * page));

    const ob = (orderBy as string) || "data";
    usp.set("order_by", ob);
    usp.set("order", order);

    if (camada) usp.set("camada", camada);
    if (propriedade) usp.set("propriedade", propriedade);
    if (valuePropriedade) usp.set("valor_propriedade", valuePropriedade);
    if (dataSearch.dataInicio)
      usp.set("data_inicio", dataSearch.dataInicio.format("YYYY-MM-DDTHH:mm:ss.SSSSSS"));
    if (dataSearch.dataFim)
      usp.set("data_fim", dataSearch.dataFim.format("YYYY-MM-DDTHH:mm:ss.SSSSSS"));
    if (descricao) usp.set("descricao", descricao);
    if (usuario) usp.set("usuario", usuario);

    const qs = usp.toString() ? `?${usp.toString()}` : "";
    window.history.replaceState({}, "", `${window.location.pathname}${qs}`);
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    camada,
    propriedade,
    valuePropriedade,
    dataSearch.dataInicio,
    dataSearch.dataFim,
    descricao,
    usuario,
  ]);

  function handleChange(
    event: SelectChangeEvent | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setFunction: React.Dispatch<any>,
    fieldName?: string,
  ) {
    const value = event.target.value as string;
    setFunction(value);
  }

  async function getAuditoria(): Promise<AuditoriaEnvelope> {
    const municipio = localStorage.getItem("municipio")?.toLowerCase();
    if (!municipio) throw new Error("Município não definido");

    const offset = page * rowsPerPage;

    const params: Record<string, any> = {
      limit: rowsPerPage,
      offset,
      order_by: (orderBy as string) || "data",
      order,
      ...(camada ? { camada } : {}),
      ...(propriedade ? { propriedade } : {}),
      ...(valuePropriedade ? { valor_propriedade: valuePropriedade } : {}),
      ...(dataSearch.dataInicio
        ? { data_inicio: dataSearch.dataInicio.format("YYYY-MM-DDTHH:mm:ss.SSSSSS") }
        : {}),
      ...(dataSearch.dataFim
        ? { data_fim: dataSearch.dataFim.format("YYYY-MM-DDTHH:mm:ss.SSSSSS") }
        : {}),
      ...(descricao ? { descricao } : {}),
      ...(usuario ? { usuario } : {}),
    };

    const response = await apiCadastro.get(`/caracterizacao/${municipio}/auditoria/search`, { params });
    return response.data as AuditoriaEnvelope;
  }

  async function getFilters(): Promise<GenericObject> {
    const response: any = await apiGateway(`/rest/`);
    return response.data.definitions;
  }

  const queryAuditorias = useQuery<AuditoriaEnvelope>({
    queryKey: [
      "auditoria",
      page,
      rowsPerPage,
      order,
      orderBy,
      camada,
      propriedade,
      valuePropriedade,
      dataSearch.dataInicio?.valueOf() || null,
      dataSearch.dataFim?.valueOf() || null,
      descricao,
      usuario,
    ],
    queryFn: () => getAuditoria(),
    staleTime: 60000,
  });

  const queryFiltros = useQuery({ queryKey: ["filtros"], queryFn: () => getFilters(), staleTime: 120000 });

  function handleChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
    setPage(newPage);
  }

  function handleSearch() {
    if (page !== 0) setPage(0);
    else queryAuditorias.refetch();
  }

  function handleRequestSort(property: keyof Auditoria) {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }

  function isValidSearch() {
    return (
      valuePropriedade === "" &&
      camada === "" &&
      (dataSearch.dataInicio === null || dataSearch.dataFim === null) &&
      descricao === "" &&
      usuario === ""
    );
  }

  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  function handleChangeDataInicio(date: moment.Moment | null) {
    const newData = { ...dataSearch, dataInicio: date };
    setDataSearch(newData);
    if (date && dataSearch.dataFim && date.isAfter(dataSearch.dataFim)) {
      setDataSearch(newData);
    }
  }

  function handleChangeDataFim(date: moment.Moment | null) {
    const newData = { ...dataSearch, dataFim: date };
    if (date && dataSearch.dataInicio && date.isBefore(dataSearch.dataInicio)) {
      alert("A data de fim não pode ser menor que a data de início.");
      return;
    }
    setDataSearch(newData);
  }

  function clearFilters() {
    setCamada("");
    setPropriedade("");
    setValuePropriedade("");
    setDataSearch({ dataInicio: null, dataFim: null });
    setDescricao("");
    setUsuario("");
    setPage(0);
  }

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  async function exportConsult(format: string) {
    setIsExporting(true);
    handleClose();

    try {
      const auditoriaExport = await getAuditoriaExport();

      if (auditoriaExport.length < 1) {
        toast("Nenhum item encontrado para o filtro aplicado!");
        setIsExporting(false);
        return;
      }

      if (format === "csv") {
        exportToCSV(auditoriaExport);
        toast.success("Consulta exportada com sucesso!");
      } else if (format === "xlsx") {
        exportToXLSX(auditoriaExport);
        toast.success("Consulta exportada com sucesso!");
      } else {
        toast("Formato não suportado!");
      }

      setIsExporting(false);
    } catch {
      toast("Um erro inesperado aconteceu, tente novamente!");
      setIsExporting(false);
    }
  }

  function exportToCSV(data: Record<string, any>) {
    const expandedData = data.map((row) => {
      const flattenedRow: Record<string, any> = {};
      Object.keys(row).forEach((key) => {
        if (typeof row[key] === "object" && row[key] !== null) {
          Object.keys(row[key]).forEach((subKey) => {
            if (row[key][subKey] !== null) {
              flattenedRow[`${key}_${subKey}`] = row[key][subKey];
            }
          });
        } else if (row[key] !== null) {
          flattenedRow[key] = row[key];
        }
      });
      return flattenedRow;
    });

    const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{1,6})?)?$/;

    expandedData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const value = row[key];
        if (typeof value === "string" && datePattern.test(value)) {
          const date = new Date(value);
          row[key] = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date
            .getMinutes()
            .toString()
            .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
        }
      });
    });

    const allHeaders = new Set<string>();
    expandedData.forEach((row) => {
      Object.keys(row).forEach((key) => allHeaders.add(key));
    });
    const headers = Array.from(allHeaders);

    const csvRows = expandedData.map((row) =>
      headers
        .map((header) => {
          const cellValue = row[header];
          return typeof cellValue === "string" ? `"${cellValue}"` : cellValue;
        })
        .join(","),
    );

    const csvContent = [`"${headers.join('","')}"`, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "auditoria_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function exportToXLSX(data: Record<string, any>) {
    const expandedData = data.map((row) => {
      const flattenedRow: any = {};
      Object.keys(row).forEach((key) => {
        if (typeof row[key] === "object" && row[key] !== null) {
          Object.keys(row[key]).forEach((subKey) => {
            if (row[key][subKey] !== null) {
              flattenedRow[`${key}_${subKey}`] = row[key][subKey];
            }
          });
        } else if (row[key] !== null) {
          flattenedRow[key] = row[key];
        }
      });
      return flattenedRow;
    });

    const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{1,6})?)?$/;

    expandedData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const value = row[key];
        if (typeof value === "string" && datePattern.test(value)) {
          const date = new Date(value);
          row[key] = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date
            .getMinutes()
            .toString()
            .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
        }
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(expandedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AuditoriaExport");

    XLSX.writeFile(workbook, "auditoria_export.xlsx");
  }

  async function getAuditoriaExport(): Promise<Auditoria[]> {
    const municipio = localStorage.getItem("municipio")?.toLowerCase();
    if (!municipio) throw new Error("Município não definido");

    const params: Record<string, any> = {
      limit: 100000,
      offset: 0,
      order_by: (orderBy as string) || "data",
      order,
      ...(camada ? { camada } : {}),
      ...(propriedade ? { propriedade } : {}),
      ...(valuePropriedade ? { valor_propriedade: valuePropriedade } : {}),
      ...(dataSearch.dataInicio
        ? { data_inicio: dataSearch.dataInicio.format("YYYY-MM-DDTHH:mm:ss.SSSSSS") }
        : {}),
      ...(dataSearch.dataFim
        ? { data_fim: dataSearch.dataFim.format("YYYY-MM-DDTHH:mm:ss.SSSSSS") }
        : {}),
      ...(descricao ? { descricao } : {}),
      ...(usuario ? { usuario } : {}),
    };

    const res = await apiCadastro.get(`/caracterizacao/${municipio}/auditoria/search`, { params });
    return (res.data?.data ?? []) as Auditoria[];
  }

  const [metadata, setMetadata] = React.useState<GenericObject[]>([]);

  async function fetchForms() {
    const forms = await getForms();
    if (forms) {
      setMetadata(extractCampos(forms));
    }
  }

  function extractCampos(data: GenericObject) {
    return Object.values(data).flatMap((itemArray) =>
      itemArray.flatMap((item: GenericObject) => item.projetosCamadasFormulariosCampos),
    );
  }

  React.useEffect(() => {
    fetchForms();
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexWrap: "wrap",
          alignItems: "center",
          width: "90%",
          margin: "auto",
          marginBottom: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 3 }}>
          <Box sx={{ minWidth: "110px" }}>
            <FormControl fullWidth>
              <InputLabel id="listCamada">Camadas</InputLabel>
              <Select
                labelId="listCamada"
                id="listCamadaSelect"
                value={camada}
                label="Camadas"
                onChange={(e) => handleChange(e, setCamada, "camada")}
                displayEmpty
              >
                {camada && (!queryFiltros.data || !Object.keys(queryFiltros.data).includes(camada)) && (
                  <MenuItem value={camada} key="temp-camada">
                    {camada}
                  </MenuItem>
                )}
                {queryFiltros.data &&
                  Object.keys(queryFiltros.data).length > 0 &&
                  Object.keys(queryFiltros.data)
                    .sort((a, b) => a.localeCompare(b))
                    .map((camada: string, index: number) => (
                      <MenuItem key={index} value={camada}>
                        {camada}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <FormControl sx={{ minWidth: "140px" }}>
              <InputLabel id="listCamada">Propriedades</InputLabel>

              <Select
                labelId="listPropriedades"
                id="listPropriedadesSelect"
                value={propriedade}
                label="Propriedades"
                onChange={(e) => handleChange(e, setPropriedade, "propriedade")}
                disabled={camada === ""}
              >
                {camada !== "" &&
                  queryFiltros.data &&
                  Object.keys(queryFiltros.data[camada]).length > 0 &&
                  Object.keys(queryFiltros.data[camada].properties)
                    .sort((a, b) => a.localeCompare(b))
                    .map((propriedade: string, index: number) => (
                      <MenuItem key={index} value={propriedade}>
                        {propriedade}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>

            <TextField
              id="search-propriedade"
              sx={{ minWidth: "140px" }}
              label="Pesquisar Propriedade"
              variant="outlined"
              type="search"
              value={valuePropriedade}
              disabled={propriedade === ""}
              onChange={(e) => handleChange(e, setValuePropriedade, "valor_propriedade")}
            />
          </Box>

          <Box>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ display: "flex", gap: 3 }}>
                <DateTimePicker
                  disableFuture
                  label="Data de Início"
                  format="L HH:mm"
                  value={dataSearch.dataInicio}
                  onChange={handleChangeDataInicio}
                />

                <DateTimePicker
                  disableFuture
                  label="Data de Fim"
                  format="L HH:mm"
                  value={dataSearch.dataFim}
                  onChange={handleChangeDataFim}
                />
              </Box>
            </LocalizationProvider>
          </Box>

          <Box>
            <TextField
              id="search-descrição"
              label="Pesquisar Descrição"
              variant="outlined"
              fullWidth
              type="search"
              value={descricao}
              onChange={(e) => handleChange(e, setDescricao, "descricao")}
            />
          </Box>

          <Box>
            <TextField
              id="search-usuário"
              label="Pesquisar Usuário"
              variant="outlined"
              fullWidth
              value={usuario}
              type="search"
              onChange={(e) => handleChange(e, setUsuario, "usuario")}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 3, marginTop: 3 }}>
          <Button color="success" onClick={() => alert("Em breve...")} disabled variant="contained">
            Adicionar Filtro
          </Button>

          <Button
            color="success"
            onClick={() => handleSearch()}
            sx={{ width: "143.7px" }}
            variant="contained"
            disabled={isValidSearch()}
          >
            Filtrar
          </Button>

          <Button color="warning" onClick={() => clearFilters()} sx={{ width: "143.7px" }} variant="contained">
            Limpar Filtros
          </Button>

          <Button color="secondary" onClick={handleClick} variant="contained" disabled={isExporting}>
            {!isExporting ? "Exportar Consulta" : "Carregando..."}
          </Button>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem onClick={() => exportConsult("xlsx")}>XLSX</MenuItem>
            <MenuItem onClick={() => exportConsult("csv")}>CSV</MenuItem>
          </Menu>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ width: "90%", margin: "auto" }}>
        <Table aria-label="Tabela de auditorias de cadastro">
          <TableHead>
            <TableRow>
              <TableCell align="center" />
              <TableCell>
                <TableSortLabel
                  active={orderBy === "id"}
                  direction={orderBy === "id" ? order : "asc"}
                  onClick={() => handleRequestSort("id")}
                >
                  Id Auditoria
                </TableSortLabel>
              </TableCell>
              <TableCell>Id</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "data"}
                  direction={orderBy === "data" ? order : "asc"}
                  onClick={() => handleRequestSort("data")}
                >
                  Data
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "camada"}
                  direction={orderBy === "camada" ? order : "asc"}
                  onClick={() => handleRequestSort("camada")}
                >
                  Camada
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "descricao"}
                  direction={orderBy === "descricao" ? order : "asc"}
                  onClick={() => handleRequestSort("descricao")}
                >
                  Descrição
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "usuario"}
                  direction={orderBy === "usuario" ? order : "asc"}
                  onClick={() => handleRequestSort("usuario")}
                >
                  Usuário
                </TableSortLabel>
              </TableCell>
              <TableCell>Mensagem</TableCell>
              <TableCell>Resumo</TableCell>
              <TableCell align="center">Ver modificações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!queryAuditorias.isLoading &&
              queryAuditorias?.data?.data?.map((row) => <Row key={row.id} row={row} metadata={metadata} />)}
            {queryAuditorias.isLoading &&
              Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 10 }).map((_, indexCell) => (
                    <TableCell key={indexCell}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[1, 2, 4, 5, 10, 25, 50, 100, 200]}
                count={queryAuditorias.data?.total ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage={"Linhas por página"}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {queryAuditorias?.data?.data?.length === 0 && (
        <h1 style={{ textAlign: "center" }}>Nenhum item encontrado para o filtro aplicado</h1>
      )}
    </>
  );
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  function handleFirstPageButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    onPageChange(event, 0);
  }

  function handleBackButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    onPageChange(event, page - 1);
  }

  function handleNextButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    onPageChange(event, page + 1);
  }

  function handleLastPageButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

function Row(props: { row: Auditoria; metadata: GenericObject[] }) {
  const { row, metadata } = props;

  const [open, setOpen] = React.useState(false);
  const [openDiff, setOpenDiff] = React.useState(false);

  function normalizeBody(body: any): any {
    if (body === null || body === undefined) return body;
    if (typeof body === "string") {
      try {
        return JSON.parse(body);
      } catch {
        return null;
      }
    }
    return body;
  }

  const bodyNovo = React.useMemo(() => normalizeBody(row.body_novo), [row.body_novo]);
  const bodyAntigo = React.useMemo(() => normalizeBody(row.body_antigo), [row.body_antigo]);
  const idRegistro = (bodyNovo && bodyNovo.id) || (bodyAntigo && bodyAntigo.id) || "-";

  function haveSameKeys(obj1: any, obj2: any): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return true;
    } else {
      return false;
    }
  }

  function countChanges(body_antigo: any, body_novo: any) {
    if (!body_antigo) return "Um novo registro foi criado";
    if (!body_novo) return "Um registro foi deletado";
    try {
      let count = 0;

      for (const key in body_antigo) {
        if (body_antigo.hasOwnProperty(key) && body_novo.hasOwnProperty(key)) {
          if (JSON.stringify(body_antigo[key]) !== JSON.stringify(body_novo[key])) {
            count++;
          }
        }
      }
      return count === 0 ? "-" : `${count} modificações`;
    } catch (error) {
      return "-";
    }
  }

  function labelValue(tblNomeColuna: string, value: string) {
    const data = metadata.find((item) => item.tblNomeColuna === tblNomeColuna);
    if (data && data.camposSelect) {
      const label = data.camposSelect.find((item: GenericObject) => item.tblValorDb === String(value))?.nome;
      if (label) {
        return label;
      }
      return value;
    }
    return value;
  }

  function formatValue(key: string, value: string) {
    if (!row) return;

    if (value === null || value === undefined) {
      return "-";
    }

    if (key === "geom") {
      return "Visualize a geometria em 'Visualizar em detalhes'";
    }

    const isObject = value && typeof value === "object" && !Array.isArray(value);

    if (isObject) {
      return JSON.stringify(value, null, 2);
    }

    if (typeof value === "boolean") {
      return value ? "Sim" : "Não";
    }

    const label = labelValue(key, value);

    if (label) {
      return label;
    }

    if (value === "") {
      return "-";
    }

    return String(value);
  }

  function safeStringify(value: any): string {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  const changes = React.useMemo(() => {
    const oldObj = bodyAntigo ?? {};
    const newObj = bodyNovo ?? {};

    const keys_old = Object.keys(oldObj || {});
    const keys_new = Object.keys(newObj || {});
    const allKeys = [...keys_new, ...keys_old].filter((key, index, self) => self.indexOf(key) === index);

    return allKeys
      .filter((key) => safeStringify(oldObj?.[key]) !== safeStringify(newObj?.[key]))
      .map((key) => ({
        key,
        oldValue: oldObj?.[key],
        newValue: newObj?.[key],
      }));
  }, [bodyAntigo, bodyNovo]);

  function ModificacoesTable() {
    if (!changes.length) {
      return <Box>Nenhuma modificação encontrada.</Box>;
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Campo</TableCell>
            <TableCell align="right">Antigo</TableCell>
            <TableCell align="right">Novo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={c.key}>
              <TableCell component="th" scope="row">
                {c.key}
              </TableCell>
              <TableCell align="right" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                {formatValue(c.key, c.oldValue)}
              </TableCell>
              <TableCell align="right" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                {formatValue(c.key, c.newValue)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  function currentComponent() {
    if (!row) return;

    return <ModificacoesTable />;
  }

  function handleClick() {
    const url = appendMunicipioToUrl(`/auditoria/cadastro/${row.id}`);
    window.open(url, "_blank");
  }

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.id}</TableCell>
        <TableCell>{idRegistro}</TableCell>
        <TableCell>{formatarDataBrasilia(row.data)}</TableCell>
        <TableCell>{row.camada}</TableCell>
        <TableCell>{row.descricao}</TableCell>
        <TableCell>{row.usuario}</TableCell>
        <TableCell>{row.descricao}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", borderBottom: "none" }}>
          {countChanges(bodyAntigo, bodyNovo)}
        </TableCell>
        <TableCell align="center" sx={{ whiteSpace: "nowrap", borderBottom: "none" }}>
          <Button onClick={() => setOpenDiff(true)} variant="text" color="success">
            Ver
          </Button>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Box>
                <Box sx={{ display: "flex", gap: "12px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "6px",
                      flexDirection: "column",
                      minWidth: "200px",
                      width: "100%",
                      maxWidth: "300px",
                    }}
                  >
                    <Box>
                      <b>Descrição</b>
                    </Box>
                    <Box>
                      <b>Id auditoria: </b>
                      <span>{row?.id}</span>
                    </Box>
                    <Box>
                      <b>Id: </b>
                      <span>{idRegistro}</span>
                    </Box>
                    <Box>
                      <b>Data: </b>
                      <span>{formatarDataBrasilia(row?.data)}</span>
                    </Box>
                    <Box>
                      <b>Camada: </b>
                      <span>{row?.camada}</span>
                    </Box>
                    <Box>
                      <b>Descrição: </b>
                      <span>{row?.descricao}</span>
                    </Box>
                    <Box>
                      <b>Usuário: </b>
                      <span>{row.usuario}</span>
                    </Box>
                    <Box>
                      <b>Mensagem: </b>
                      <span>-</span>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: "6px", flexDirection: "column", width: "100%" }}>
                    <b>Valores modificados</b>
                    {currentComponent()}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    marginTop: "12px",
                  }}
                >
                  <Button onClick={handleClick} variant="contained" color="success">
                    Visualizar em detalhes
                  </Button>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog open={openDiff} onClose={() => setOpenDiff(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modificações</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
            <Box>
              <b>Id auditoria:</b> {row.id}
            </Box>
            <Box>
              <b>Id:</b> {idRegistro}
            </Box>
            <Box>
              <b>Camada:</b> {row.camada}
            </Box>
            <Box>
              <b>Usuário:</b> {row.usuario}
            </Box>
            <Box>
              <b>Data:</b> {formatarDataBrasilia(row.data)}
            </Box>
          </Box>
          <ModificacoesTable />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiff(false)} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
